import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Layout components
import ServiceLayout from '../../../components/layout/ServiceLayout';
import ServicePageSection from '../../../components/layout/ServicePageSection';

// UI components
import CompactFeatureCard from '../../../components/ui/CompactFeatureCard';
import ProcessStep from '../../../components/ui/ProcessStep';
import Card from '../../../components/ui/Card';
import FAQ from '../../../components/ui/FAQ';

// Section components
import Hero from '../../../components/sections/Hero';
import CallToAction from '../../../components/sections/CallToAction';

// Data
import { caseStudies, faqs } from '../../../data/mockData';

/**
 * CRM & Pipeline Automation Service Page
 */
const CrmPipelineAutomationPage: React.FC = () => {
  const relevantCaseStudies = caseStudies.filter(
    (study) => study.solution === 'CRM & Pipeline Automation'
  );

  const relevantFaqs = faqs.filter((_, index) => [1, 2, 5, 6].includes(index));

  const keyBenefits = [
    {
      title: 'Convert More Leads Without More Staff',
      description:
        'AI-powered intake and follow-up automation respond to leads in minutes — not days — so you never lose a deal to slow response times again.',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
    },
    {
      title: 'Keep Your Pipeline Moving 24/7',
      description:
        'Automated CRM sync, lead scoring, and task creation ensure your pipeline is always up to date — even when your team is offline.',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      title: 'Eliminate Revenue-Killing Gaps',
      description:
        'Missed follow-ups and slow intake are the biggest revenue leaks in B2B service businesses. We close those gaps with automation that never forgets.',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ];

  const processSteps = [
    {
      step: 1,
      title: 'Pipeline & CRM Audit',
      description:
        'We map your current lead journey — from first contact to closed deal — to identify every manual touchpoint, delay, and missed follow-up in your pipeline.',
    },
    {
      step: 2,
      title: 'Automation Blueprint',
      description:
        'We design a tailored automation blueprint covering intake routing, CRM data entry, lead scoring, and follow-up sequences matched to your sales process.',
    },
    {
      step: 3,
      title: 'CRM Integration & Build',
      description:
        'We connect your CRM (HubSpot, Salesforce, Pipedrive, Zoho, or others) and build the automation workflows using AI tools, Zapier, Make, or direct APIs.',
    },
    {
      step: 4,
      title: 'Testing & Go-Live',
      description:
        'Every automation is stress-tested with real lead data before going live. We verify accuracy, timing, and CRM sync to ensure nothing slips through.',
    },
    {
      step: 5,
      title: 'Monitoring & Optimisation',
      description:
        'Post-launch, we monitor conversion rates and pipeline velocity, continuously refining sequences to improve close rates over time.',
    },
  ];

  const automationCapabilities = [
    {
      title: 'Lead Intake Automation',
      description:
        'Auto-capture leads from web forms, ads, and emails into your CRM — with instant qualification, tagging, and assignment to the right rep.',
    },
    {
      title: 'Speed-to-Lead Response',
      description:
        'AI-triggered responses reach new leads within 60 seconds of inquiry, dramatically increasing qualification rates and first-call engagement.',
    },
    {
      title: 'CRM Data Entry & Enrichment',
      description:
        'Eliminate manual data entry. Leads are automatically created, enriched with company data, and scored before your team even sees them.',
    },
    {
      title: 'Follow-Up Sequence Automation',
      description:
        'Multi-channel follow-up sequences (email, SMS, task reminders) that run automatically until a lead responds, books, or opts out.',
    },
    {
      title: 'Pipeline Stage Automation',
      description:
        'Auto-advance deals through pipeline stages based on activity triggers — meeting booked, proposal sent, contract opened — with zero manual updates.',
    },
    {
      title: 'Re-engagement Campaigns',
      description:
        'Automatically re-engage cold leads and stalled deals with AI-personalised outreach sequences, turning dead pipeline into active revenue.',
    },
  ];

  const crmIntegrations = [
    { name: 'HubSpot', tier: 'Full Integration' },
    { name: 'Salesforce', tier: 'Full Integration' },
    { name: 'Pipedrive', tier: 'Full Integration' },
    { name: 'Zoho CRM', tier: 'Full Integration' },
    { name: 'Zapier / Make', tier: 'Connector Layer' },
    { name: 'Custom CRMs', tier: 'API Integration' },
  ];

  const impactMetrics = [
    { value: '3x', label: 'Average pipeline conversion increase' },
    { value: '4 min', label: 'Average speed-to-lead response time' },
    { value: '0', label: 'Additional headcount required' },
  ];

  return (
    <ServiceLayout>
      <Hero
        title="CRM & Pipeline Automation"
        subtitle="Build AI-powered growth systems that turn B2B leads into revenue — automating your pipeline, intake, and follow-up without adding headcount."
        ctaText="Book a Free Pipeline Audit"
        ctaLink="/contact"
        backgroundType="gradient"
      />

      {/* Key Benefits */}
      <ServicePageSection title="Revenue Growth Without the Overhead" bgColor="gray" padding="md">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {keyBenefits.map((benefit, index) => (
            <CompactFeatureCard
              key={benefit.title}
              title={benefit.title}
              description={benefit.description}
              icon={benefit.icon}
              delay={index * 0.1}
            />
          ))}
        </div>
      </ServicePageSection>

      {/* Impact Metrics */}
      <ServicePageSection title="Real Results for B2B Service Businesses" padding="md">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          {impactMetrics.map((metric, index) => (
            <motion.div
              key={metric.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="p-8">
                <div className="text-4xl font-bold text-primary mb-2">{metric.value}</div>
                <p className="text-neutral-600">{metric.label}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </ServicePageSection>

      {/* Our Process */}
      <ServicePageSection title="Our Pipeline Automation Process" bgColor="gray" padding="md">
        <div className="space-y-8">
          {processSteps.map((step, index) => (
            <motion.div
              key={step.step}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <ProcessStep number={step.step} title={step.title} description={step.description} />
            </motion.div>
          ))}
        </div>
      </ServicePageSection>

      {/* Automation Capabilities */}
      <ServicePageSection title="What We Automate" padding="md">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {automationCapabilities.map((capability, index) => (
            <motion.div
              key={capability.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="h-full p-6 hover:shadow-lg transition-shadow duration-200">
                <h3 className="text-lg font-semibold mb-3">{capability.title}</h3>
                <p className="text-neutral-600 text-sm leading-relaxed">{capability.description}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </ServicePageSection>

      {/* CRM Integrations */}
      <ServicePageSection title="CRM Integrations We Support" bgColor="gray" padding="md">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {crmIntegrations.map((crm, index) => (
            <motion.div
              key={crm.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.07 }}
            >
              <Card className="p-5 text-center">
                <div className="text-lg font-semibold mb-1">{crm.name}</div>
                <div className="text-xs text-neutral-500">{crm.tier}</div>
              </Card>
            </motion.div>
          ))}
        </div>
        <p className="text-center text-neutral-500 text-sm mt-6">
          Don't see your CRM? We support any platform with an API or Zapier/Make connector.{' '}
          <Link to="/contact" className="text-primary font-medium hover:underline">
            Ask us about your stack.
          </Link>
        </p>
      </ServicePageSection>

      {/* Case Studies */}
      {relevantCaseStudies.length > 0 && (
        <ServicePageSection title="Pipeline Automation in Action" padding="md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {relevantCaseStudies.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={study.url} className="block h-full">
                  <Card hover className="h-full p-6">
                    <div className="mb-3">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        {study.solution}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{study.title}</h3>
                    <p className="text-neutral-600 text-sm mb-4">{study.excerpt}</p>
                    <div className="space-y-2">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="flex items-start text-sm">
                          <svg className="w-4 h-4 text-primary mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-neutral-600">{result}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-4 border-t border-neutral-100">
                      <span className="inline-flex items-center text-primary font-medium text-sm">
                        Read full case study
                        <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/case-studies" className="inline-flex items-center text-primary font-medium hover:text-primary-700 transition-colors">
              View all case studies
              <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </ServicePageSection>
      )}

      {/* FAQ */}
      <ServicePageSection title="Frequently Asked Questions" bgColor="gray" padding="md">
        <div className="max-w-3xl mx-auto space-y-6">
          {relevantFaqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <FAQ items={[{ question: faq.question, answer: faq.answer }]} />
            </motion.div>
          ))}
        </div>
      </ServicePageSection>

      <CallToAction
        title="Ready to turn your pipeline into a revenue machine?"
        description="Book a free pipeline audit and we'll show you exactly where automation can close the gaps in your lead-to-revenue process."
        ctaText="Book a Free Pipeline Audit"
        ctaLink="/contact"
      />
    </ServiceLayout>
  );
};

export default CrmPipelineAutomationPage;
